/**
 * LWPOLYLINE serializer. Bulges are preserved (§3.2 decision 3): the data
 * bag's `vertices` + `bulges` go straight into the per-vertex 42 codes —
 * entityToPolyline's flattening is NOT used for writing. rectangle and
 * polygon payloads are normalized to vertex lists here.
 */

import type { Entity } from '../../../../state/store';
import type { DxfWriteContext } from '../types';
import type { DxfStreamWriter } from '../DxfStreamWriter';
import { entityPrologue } from './common';

/** Unified vertex list for the polyline-family payload shapes. */
export function polylineVertices(e: Entity): { verts: number[][]; closed: boolean; bulges: number[] } {
    const d = e.data as Record<string, unknown>;
    if (e.type === 'rectangle' && Array.isArray(d.corner1) && Array.isArray(d.corner2)) {
        const [x1, y1, z1] = d.corner1 as number[];
        const [x2, y2] = d.corner2 as number[];
        const z = z1 ?? 0;
        return {
            verts: [[x1, y1, z], [x2, y1, z], [x2, y2, z], [x1, y2, z]],
            closed: true,
            bulges: [],
        };
    }
    const verts = Array.isArray(d.vertices) ? (d.vertices as number[][]) : [];
    const bulges = Array.isArray(d.bulges) ? (d.bulges as number[]) : [];
    const closed = e.type === 'polygon' ? true : !!d.closed;
    return { verts, closed, bulges };
}

export function writeLwpolyline(w: DxfStreamWriter, ctx: DxfWriteContext, e: Entity, owner: string): void {
    const { verts, closed, bulges } = polylineVertices(e);
    if (verts.length < 2) return;
    const d = e.data as Record<string, unknown>;
    entityPrologue(w, ctx, e, 'LWPOLYLINE', owner);
    w.pair(100, 'AcDbPolyline');
    w.pair(90, verts.length);
    w.pair(70, closed ? 1 : 0);
    // Global PEDIT width (43) — only when actually set.
    if (typeof d.width === 'number' && d.width > 0) w.pair(43, d.width);
    verts.forEach((v, i) => {
        w.pair(10, v[0] ?? 0).pair(20, v[1] ?? 0);
        const bulge = bulges[i];
        if (typeof bulge === 'number' && bulge !== 0) w.pair(42, bulge);
    });
}
